import { useState } from 'react';
import { HelpCircle, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';

const FAQS = [
  {
    q: 'How do I confirm that my application and attached documents have been received?',
    a: 'Once you submit your application, you will receive a system-generated response that announces successful submission of your application. Please note that such automated responses may drop in your spam/junk box. Kindly check your junk filter or your email preference settings. Your status on the application portal will also indicate successful submission of your application.',
  },
  {
    q: 'Should I expect to be contacted by Human Capital Management (HCM) on the status of my application?',
    a: "No. However, you can log on to your online application portal, and the status of your application can be viewed on the candidate's dashboard.",
  },
  {
    q: 'What browsers should I use to apply?',
    a: 'For the best experience and optimal security, we strongly recommend using the latest stable versions of Google Chrome, Microsoft Edge, and Mozilla Firefox. These browsers regularly update to provide the newest features, security enhancements, and performance improvements. For further support, please contact the IT Help Desk at helpdesk@nnpcgroup.com.',
  },
  {
    q: 'Can I apply for more than one position?',
    a: 'Yes. Eligible candidates may apply for a maximum of two (2) positions in this Exercise. You may apply with your mobile phone if comfortable; however, using a computer is advisable for a better user experience.',
  },
  {
    q: 'Do I need to have an email address to apply?',
    a: 'All information about the exercise will be disseminated via your official NNPC Limited email address.',
  },
  {
    q: 'What should I do if I forget my log-in password for the application portal?',
    a: 'Your log-in password is your NNPC Limited Domain password. For support, contact the NNPC IT Help desk at 09122222707.',
  },
  {
    q: 'What are the specifications for attaching my resume and other documents?',
    a: 'Your resume will be system-generated after completing the required fields. Other documents must be in pdf, jpg, jpeg, tif, or png format and not exceed 200kb each.',
  },
  {
    q: 'Do I have to submit a cover letter to support my application?',
    a: 'No, you are not required to submit a cover letter.',
  },
  {
    q: 'Will I be required to provide references in my application?',
    a: 'No, you do not need to provide references in your application.',
  },
  {
    q: 'Can I be sure that the confidentiality of my personal data will be maintained?',
    a: 'In compliance with NNPC Limited Employee Information Confidentiality and Protection Guideline, we will handle your personal data with the utmost commitment to confidentiality.',
  },
  {
    q: 'My computer times out without submitting my application. What do I do?',
    a: 'The information entered is automatically saved on the Application portal. Log-in again to continue. If unable, contact resourcing Help desk at 08039990033 or recruitment@nnpcgroup.com.',
  },
  {
    q: 'How do I make changes to my application?',
    a: 'Log into your application account with your staff ID and password. Click "Edit Application," make necessary changes, and save. Changes cannot be made after submission.',
  },
  {
    q: 'How do I confirm my application is successfully submitted?',
    a: 'You will receive a confirmation notification upon successful submission.',
  },
  {
    q: 'How long will the portal be opened for application?',
    a: 'The application portal will be open from 8th – 22nd May 2026 after which it will close.',
  },
  {
    q: 'How do I ascertain my eligibility to apply for a position?',
    a: 'Check the eligibility criteria published via the Mail Broadcast of 8th May 2026.',
  },
  {
    q: 'Can I apply for a position in a different BU/SU?',
    a: 'Yes, an eligible candidate may apply for a position in another BU/SU.',
  },
  {
    q: 'Can I apply for a position that is two levels above my current grade level?',
    a: 'No, you may only apply for jobs one level above your current grade level or on your current grade level subject to the eligibility criteria.',
  },
  {
    q: 'On what basis will candidates be selected for interviews?',
    a: 'Candidates will be invited for interviews based on the successful outcome of the screening process.',
  },
  {
    q: 'What are the considerations for staff who will soon retire and cannot benefit from the exercise?',
    a: "Such staff should look forward to NNPC's magnanimous statutory retirement packages.",
  },
  {
    q: 'Will my score for position A apply to position B if it is higher?',
    a: 'No. Each score for the interview is distinct to the position applied for and is not transferrable.',
  },
  {
    q: 'If I am not eligible for this Round, what is my fate?',
    a: 'Progression or lateral movement into senior management positions will be a recurring exercise provided you meet the eligibility criteria.',
  },
  {
    q: 'If I am not successful in this round exercise, what can I expect next?',
    a: 'You wait for another opportunity to participate provided you meet the eligibility criteria for the future exercise.',
  },
  {
    q: 'What is your advice to staff that are prepping for the exercise?',
    a: '• Carry out self-assessment to determine what job you are best fit for. • Read and digest the mail broadcast. Prepare adequately to prove that you are the best fit for any position you have applied for.',
  },
  {
    q: 'What if I cannot connect to the Application Portal?',
    a: "Make sure your PC is connected to NNPC Limited's network",
  },
  {
    q: 'What do I do if I cannot login to the Application Portals?',
    a: 'Make sure your ID and domain password is correct. Use NNPC domain credentials to log on.',
  },
  {
    q: 'Who do I contact if the Application Portal is not displaying my correct information?',
    a: 'Please send an email to recruitment@nnpcgroup.com. You can also call HCM help desk on 08039990033.',
  },
  {
    q: 'How do I channel further enquiries?',
    a: 'Please send an email to recruitment@nnpcgroup.com. You can also call HCM help desk on 08039990033 or IT help desk on 09122222707',
  },
];

export default function FaqWidget() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = search.trim()
    ? FAQS.filter(
        (f) =>
          f.q.toLowerCase().includes(search.toLowerCase()) ||
          f.a.toLowerCase().includes(search.toLowerCase()),
      )
    : FAQS;

  return (
    <>
      {/* Floating trigger button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-4 py-3 text-sm font-semibold hover:bg-primary/90 transition-all hover:shadow-xl active:scale-95"
        aria-label="Open FAQ"
      >
        <HelpCircle className="h-4 w-4" />
        <span>Help / FAQ</span>
      </button>

      {/* Overlay panel */}
      {open && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />

          {/* Slide-in panel */}
          <div className="fixed right-0 top-0 z-50 h-dvh w-full max-w-lg bg-background shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/60 shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
                  <HelpCircle className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="font-semibold text-sm">
                    Frequently Asked Questions
                  </p>
                  <p className="text-xs text-muted-foreground">
                    EMMR Portal
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setOpen(false)}
                aria-label="Close FAQ panel"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>

            {/* Search */}
            <div className="px-5 py-3 border-b border-border/40 shrink-0">
              <Input
                placeholder="Search questions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9 text-sm"
              />
            </div>

            {/* FAQ list */}
            <ScrollArea className="flex-1 px-5 py-2">
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No results for "{search}"
                </p>
              ) : (
                <Accordion type="single" collapsible className="space-y-1 pb-6">
                  {filtered.map((faq, i) => {
                    const globalIndex = FAQS.indexOf(faq);
                    const num = globalIndex >= 0 ? globalIndex + 1 : i + 1;
                    return (
                      <AccordionItem
                        key={i}
                        value={`faq-${i}`}
                        className="border border-border/50 rounded-lg px-4 bg-muted/20 hover:bg-muted/40 transition-colors"
                      >
                        <AccordionTrigger className="text-sm font-medium text-left py-3 hover:no-underline gap-3">
                          <span className="shrink-0 text-muted-foreground font-semibold tabular-nums">
                            {num}.
                          </span>
                          <span>{faq.q}</span>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line pb-3">
                          {faq.a}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              )}
            </ScrollArea>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-border/40 bg-muted/20 shrink-0">
              <p className="text-xs text-muted-foreground text-center">
                Still need help? Email{' '}
                <a
                  href="mailto:recruitment@nnpcgroup.com"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  recruitment@nnpcgroup.com
                </a>{' '}
                or call <span className="font-medium">08039990033</span>
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}
